<template>
  <page>
    <div class="cuscd-item">
      <div v-if="loading" class="cuscd-item-inner cuscd-rounded loading-item">
        <Loader></Loader>
      </div>
      <div v-else class="cuscd-item-inner cuscd-rounded documents-tile">
        <div class="documents-tile-content">
          <span>
            <h1 class="cuscd-h2style cuscd-item-header">
              My Documents
              <info-tooltip>
                <template #tooltipText>
                  <p>
                    When opening Application Documents, please be sure your pop-up blocker is turned
                    off. You can also download the documents by selecting the download button.
                    Uploaded Documents are not viewable in your portal.
                  </p>
                </template>
              </info-tooltip>
            </h1>
          </span>
          <div class="documents-section">
            <p class="cuscd-h2style cuscd-item-header">Application Documents</p>
            <div class="table-wrapper" v-if="generatedDocuments?.length > 0">
              <table class="table generatedDocuments">
                <tr v-for="document in generatedDocuments" :key="document.documentId">
                  <td :class="[!document?.documentTypeName ? 'bold' : '']">
                    {{
                      document?.documentTypeName
                        ? document?.documentTypeName
                        : document?.documentName
                    }}
                    <info-tooltip v-show="showDocumentNameTooltip(document)">
                      <template #tooltipText
                        ><p>
                          {{ documentNameTooltipText(document) }}
                        </p>
                      </template></info-tooltip
                    >
                  </td>
                  <td>
                    <div class="createdAtWrapper">
                      <span>{{ document?.createdAt }}</span>
                      <span>
                        <a
                          v-if="document.isDownloadable"
                          class="download"
                          @click="download(document)"
                          :disabled="downloading"
                        >
                          <font-awesome-icon
                            class="download-icon"
                            :icon="['fas', 'cloud-arrow-down']"
                          />&nbsp;</a
                        >
                        <a
                          :ref="
                            el => {
                              linkRefs[getRefId(document)] = el;
                            }
                          "
                        ></a>
                      </span>
                      <span>
                        <div class="open-document-in-new-tab">
                          <a
                            class="link"
                            @click="openDocumentInNewTab(document)"
                            :disabled="retrievingDocument"
                            >Open Document in New Tab</a
                          >
                          <p class="disclaimer" v-if="document?.showViewDocumentDisclaimerText">
                            Not seeing your document? Make sure you turn off pop-up blockers and use
                            the most recent version of your browser. If you’re still having issues,
                            please call us at
                            <a class="phone" :href="`tel:+${tollFreeNumberHref}`">{{
                              tollFreeNumber
                            }}</a
                            >.
                          </p>
                        </div>
                      </span>
                    </div>
                  </td>
                </tr>
              </table>
            </div>
            <div class="no-documents" v-else>
              There Are No Application Documents Associated With This Application
            </div>
          </div>
          <div class="documents-section">
            <div>
              <p class="cuscd-h2style cuscd-item-header">Uploaded Documents</p>
              <div class="table-wrapper" v-if="paginatedItems?.length > 0">
                <table class="table">
                  <thead>
                    <tr>
                      <th
                        style="min-width: 40%"
                        scope="col"
                        @click="sortDocuments('name')"
                        :class="sortClasses('name')"
                      >
                        Name
                      </th>
                      <th scope="col" @click="sortDocuments('size')" :class="sortClasses('size')">
                        Size
                      </th>
                      <th
                        scope="col"
                        @click="sortDocuments('createdAt')"
                        :class="sortClasses('createdAt')"
                      >
                        Created On
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="document in paginatedItems" :key="document.id">
                      <td>{{ document?.name }}</td>
                      <td>{{ document?.size }}</td>
                      <td>{{ document?.createdAt || document.fileSize }}</td>
                    </tr>
                  </tbody>
                </table>
                <div class="pagination" v-if="pages.length > 1">
                  <p>Page</p>
                  <base-form-field
                    v-model="pagination.currentPage"
                    :schema="tableFieldSchema"
                    :options="pages"
                    @update:modelValue="changePage"
                  />
                  <p>of {{ pagination.totalPages }}</p>
                  <button
                    v-show="pagination.currentPage > 1"
                    type="button"
                    class="btn btn-primary"
                    @click="previous"
                  >
                    Previous
                  </button>
                  <button
                    v-show="pagination.currentPage < pagination.totalPages"
                    type="button"
                    class="btn btn-primary"
                    @click="next"
                  >
                    Next
                  </button>
                </div>
              </div>
              <div class="no-documents" v-else>
                There Are No Uploaded Documents Associated With This Application
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </page>
</template>
<script setup lang="ts">
import { ref, reactive, computed, onMounted, nextTick, watch } from "vue";
import { useStore } from "vuex";
import Loader from "@/components/shared/Loader.vue";
import Page from "@/components/shared/layout/Page.vue";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import useFormatter from "@/use/formatter";
import useFileUtilities from "@/use/fileUtilities";
import { FieldSchema, FormField, Option } from "@/models/form";
import useApplications from "@/use/application";
import Error from "@/components/shared/Error.vue";
import { $api } from "@/services/api";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import InfoTooltip from "@/components/shared/tooltips/InfoTooltip.vue";
import { DocumentNameConstants } from "@/config/constants";
import { DocumentStatuses } from "@/config/enums";

const store = useStore();
const { base64ToBlob } = useFileUtilities();

// Reactive state
const loading = ref<boolean>(true);
const downloading = ref<boolean>(false);
const retrievingDocument = ref<boolean>(false);

let documents = reactive([]) as any[];
let uploadedDocuments = reactive([]) as any[];
let generatedDocuments = reactive([]) as any[];
const { formatDateTime, formatBytes } = useFormatter();
const buttonTypes = computed(() => {
  let types = ["uploaded", "generated"];
  return types;
});
const documentsState = ref({ selected: "uploaded" as string, documents: [] as any[] });
const linkRefs = reactive({} as any);
const oidcUser = computed(() => store.getters["oidcStore/oidcUser"]);
const oidcIsAuthenticated = computed(() => store.getters["oidcStore/oidcIsAuthenticated"]);
const tollFreeNumberHref = computed<string>(() => store.getters["lenderTollFreeNumberHref"]);
const tollFreeNumber = computed<string>(() => store.getters["lenderTollFreeNumber"]);
const applicationId = computed<number>(() => store.state.appData?.application?.id);

// Methods
async function loadDocuments() {
  window.scrollTo(0, 0);

  // authenticated user only
  if (oidcUser.value && oidcIsAuthenticated.value) {
    if (
      store.state.appData.applicantDocuments === undefined ||
      store.state.appData.applicantDocuments.length === 0 ||
      store.state.appData.generatedDocuments === undefined ||
      store.state.appData.generatedDocuments.length === 0
    ) {
      await store.dispatch("rehydrateStore");
    }
    if (store.state.appData.applicantDocuments.length > 0) {
      uploadedDocuments = store.state.appData.applicantDocuments
        .filter(
          (d: any) =>
            [Number(DocumentStatuses.Signed), Number(DocumentStatuses.Generated)].includes(
              d.documentStatusId,
            ) === false,
        )
        .map((doc: any) => {
          return {
            name: doc?.uploadedDocumentMetadata?.originalFileName
              ? doc?.uploadedDocumentMetadata?.originalFileName
              : "Not Available",
            size: doc?.uploadedDocumentMetadata?.fileLength
              ? formatBytes(doc?.uploadedDocumentMetadata?.fileLength)
              : "Not Available",
            createdAt: doc?.createdAt ? formatDateTime(doc?.createdAt) : "Not Available",
            documentId: doc?.documentId,
            documentType: doc?.documentType,
          };
        });
    }
    if (store.state.appData.generatedDocuments.length > 0) {
      generatedDocuments = store.state.appData.generatedDocuments;
    }
  }

  loading.value = false;
}
async function previous() {
  if (pagination.currentPage > 1) {
    pagination.currentPage--;
    loading.value = true;
    await loadDocuments();
  }
}
async function next() {
  if (pagination.currentPage < pagination.totalPages) {
    window.scrollTo(0, 0);
    pagination.currentPage++;
    loading.value = true;
    await loadDocuments();
  }
}
async function changePage(newPage: string) {
  window.scrollTo(0, 0);
  pagination.currentPage = parseInt(newPage);
  loading.value = true;
  await loadDocuments();
}
async function openDocumentInNewTab(document: any) {
  retrievingDocument.value = true;
  document.showViewDocumentDisclaimerText = false;

  // Open window synchronously before await — required for iOS Safari popup policy
  const newWindow = window.open("", "_blank");

  let documentViewLink = "";
  if (document?.documentName === DocumentNameConstants.DESIGNATION_OF_AUTHORIZED_INDIVIDUAL_FORM) {
    documentViewLink = document?.cdnDocumentUrl;
  } else {
    documentViewLink = await $api.documents.generateDocumentViewLink({
      applicantId: document.applicantId,
      applicationId: applicationId.value,
      documentName: document.documentName,
      documentId: document.documentId,
    });
  }

  if (documentViewLink && newWindow) {
    newWindow.location.href = documentViewLink;
  } else {
    newWindow?.close();
  }
  document.showViewDocumentDisclaimerText = true;
  retrievingDocument.value = false;
}
async function getDownloadLink(document: any) {
  if (document?.documentName === DocumentNameConstants.DESIGNATION_OF_AUTHORIZED_INDIVIDUAL_FORM) {
    return document?.cdnDocumentUrl;
  } else {
    return await $api.documents.downloadDocument({ documentId: document.documentId });
  }
}
async function download(document: any) {
  downloading.value = true;
  nextTick(async () => {
    let refId = getRefId(document);
    let link = linkRefs[refId] as HTMLAnchorElement;
    let downloadLink = await getDownloadLink(document);

    if (downloadLink) {
      let a = link as HTMLAnchorElement;
      a.setAttribute("download", `${document?.documentName?.replace(/ /g, "")}`);
      a.setAttribute("href", `${downloadLink}`);
      a.setAttribute("target", "_blank");
      a.click();
      a.removeAttribute("href");
      a.removeAttribute("download");
      a.removeAttribute("target");
    } else {
      console.log("FAILED TO DOWNLOAD");
    }

    downloading.value = false;
  });
}
function showDocumentNameTooltip(document: any) {
  return document?.documentName === DocumentNameConstants.DESIGNATION_OF_AUTHORIZED_INDIVIDUAL_FORM;
}
function documentNameTooltipText(document: any) {
  if (document?.documentName === DocumentNameConstants.DESIGNATION_OF_AUTHORIZED_INDIVIDUAL_FORM) {
    return "Optional form for you to complete if you want to designate someone to act on your behalf in the event of your death";
  } else {
    return "";
  }
}
function getRefId(document: any) {
  return `download${document.documentId}${document?.documentName?.replace(/ /g, "")}`;
}
function handleButtonClick(type: string) {
  documentsState.value.selected = type;
  documentsState.value.documents = type === "uploaded" ? uploadedDocuments : generatedDocuments;
}
function getIcon(type: string) {
  return type === "uploaded" ? "file-upload" : "file-contract";
}
function getTitle(type: string) {
  return type === "uploaded" ? "Uploaded Documents" : "Generated Documents";
}

// Table Reactive State
const tableFieldSchema = reactive({
  type: "select",
  required: false,
  hideLabel: true,
  validate: (schema: FormField) => (schema.valid = true),
} as FieldSchema);

// TOTAL PAGES
const pagination = reactive({
  currentPage: 1,
  perPage: 10,
  totalPages: computed(() => Math.ceil(documents.length / pagination.perPage)),
  pages: [],
}) as any;

const paginatedItems = computed(() => {
  const { currentPage, perPage } = pagination;

  if (!currentPage) {
    return uploadedDocuments;
  } else {
    const start = (currentPage - 1) * perPage;
    const stop = start + perPage;

    return uploadedDocuments.slice(start, stop);
  }
});

const pages = computed<Array<any>>((i: any) => {
  return [...Array(pagination.totalPages).keys()].map(i => i + 1);
});

// Sort functionality
const sort = reactive({ column: "", direction: "asc" });
function sortDocuments(column: string) {
  sort.direction = sort.column === column && sort.direction !== "asc" ? "asc" : "desc";
  sort.column = column;

  paginatedItems.value.sort((a: any, b: any) => {
    let modifier = 1;
    if (sort.direction === "desc") {
      modifier = -1;
    }
    if (a[sort.column] < b[sort.column]) {
      return -1 * modifier;
    }
    if (a[sort.column] > b[sort.column]) {
      return 1 * modifier;
    } else {
      return 0;
    }
  });
}

function sortClasses(column: string) {
  return {
    "sort-asc": sort.column === column && sort.direction === "asc",
    "sort-desc": sort.column === column && sort.direction === "desc",
  };
}

onMounted(() => {
  loadDocuments();
});
</script>
<style lang="scss" scoped>
.button {
  border: 2px solid #d7d8dd;
  background-color: white;
  color: black;
  padding: 15px 32px;
  text-align: center;
  text-decoration: none;
  font-size: 16px;
  display: inline-block;
  margin: 4px 2px;
  cursor: pointer;
  border-radius: 5px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  transition: all 0.15s;
}

.button.selected,
div.xnamebutton.selected {
  border-color: black;
  // background: black;
  background-color: var(--theme-color) !important;
  color: white;
}

div.xnamebutton.xselected div.xusericon-picture {
  border: 2px solid white;
  line-height: 34px;
}

div.xnamebutton h5 {
  height: 36px;
  line-height: 36px;
  font-family: "Montserrat", arial, sans-serif;
  font-size: 14px;
  text-transform: none;
}

.cuscd-item {
  width: 80%;
  margin: 0 auto;
}

.cuscd-item-inner.loading-item {
  min-height: 500px;
}

.table-wrapper,
.no-documents {
  padding: 0 1.5rem;
}

.table-wrapper .table {
  margin-bottom: 0;
}

.table-wrapper .table th {
  cursor: pointer;
}

.table-wrapper .table th.sort-desc:after {
  content: " ";
  position: relative;
  left: 5px;
  border: 5px solid transparent;
  top: 12px;
  border-top-color: #000;
}

.table-wrapper .table th.sort-asc:after {
  content: " ";
  position: relative;
  left: 5px;
  border: 5px solid transparent;
  bottom: 14px;
  border-bottom-color: #000;
}

.filters {
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
  padding: 0 1.5625rem 1.5rem;
}

.pagination p {
  font-size: 1rem;
}

.filters p,
.pagination p {
  margin: 0 10px;
}

.pagination button {
  margin-left: 5px;
}

.table .actions a {
  text-decoration: underline;
}

.table .actions a:hover {
  cursor: pointer;
}

.cuscd-h2style {
  padding: 1.5625rem 1.5rem 0;
}

.disclosure {
  padding-left: 1.5rem;
}

.no-applications,
.no-documents {
  font-weight: normal;
  // font-size: 1.25rem;
  // margin-bottom: 1rem;
  // margin-top: 1rem;
  text-align: left;
}

a.download {
  cursor: pointer;
}

.download-icon {
  padding-left: 5px;
  /* margin-right: 5px; */
  color: #80bdff;
  font-size: 20px;
  justify-content: center;
}

.table th {
  padding: 0;
}

.table td {
  padding-right: 0;
  padding-left: 0;
}

.createdAtWrapper {
  display: flex;

  span + span {
    margin-right: 1rem;
  }

  span:first-of-type {
    flex: none;
  }
}
.table.generatedDocuments {
  td:first-of-type {
    width: 30%;
  }
}
.open-document-in-new-tab {
  a {
    text-decoration: underline;
    font-weight: 400;
    margin: 0;
    cursor: pointer;
  }

  .link {
    color: #767676;
  }

  .disclaimer {
    font-size: 0.8rem;
    font-weight: 400;
    color: #767676;

    a {
      color: #767676;
      text-decoration: underline;
      cursor: pointer;
    }
  }
}

.bold {
  font-weight: 700;
}

@media (max-width: 992px) {
  .cuscd-item-header {
    padding: 0;
  }

  .cuscd-item {
    width: 95%;
    margin: 0 auto;
  }

  .table-wrapper {
    padding: 0;
    font-size: 0.8em;
  }

  .disclosure {
    padding-left: 0;
  }

  .table-wrapper,
  .no-documents {
    padding: 0;
  }

  .createdAtWrapper {
    span:first-of-type {
      flex: auto;
    }
  }
}

@media (max-width: 600px) {
  .createdAtWrapper {
    display: block;
  }
  .cuscd-h2style {
    margin-bottom: 0;
  }
}

/* Samsung Fold's 280px handling... */
@media (max-width: 300px) {
  .cuscd-item-inner {
    padding: 1.3rem 0.1em 0.9rem;
  }

  .table-wrapper {
    padding: 0;
    font-size: 0.7em;
  }
}
</style>
